import React, { Suspense } from 'react';
import { hydrateRoot } from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter } from 'react-router-dom';
import window from 'global/window';
import { matchPath } from 'react-router';
import document from 'global/document';
import loadable, { loadableReady } from '@loadable/component';
import { I18nextProvider } from 'react-i18next';
import i18n from './i18n';

import {
  PRELOADED_STATE_KEY,
  AWS_ENV_VARS,
  MAINTENANCE_MODE,
  ENVIRONMENT,
} from './app/common/constants';

import ClientComponentLoader from './app/common/clientComponentLoader';
import { EnvProvider } from './app/common/envContext';
import logger from './app/common/logger';
import { defaultRoutes, maintenanceRoutes } from './app/common/routerConfig';
import { initCjs } from './app/common/cjsAnalytics';

// Lazy load App
const App = loadable(() => import(/* webpackChunkName: "App" */ './App'));

// Setup env
// eslint-disable-next-line no-underscore-dangle
const preLoadState = window.__PRELOADED_STATE__;
// eslint-disable-next-line no-underscore-dangle
delete window.__PRELOADED_STATE__;
const preloadedState = window[PRELOADED_STATE_KEY] || {};
const defaultEnvData = {};

Object.keys(AWS_ENV_VARS).forEach((key) => {
  defaultEnvData[key] = AWS_ENV_VARS[key].default;
});

const env = preloadedState?.envData || defaultEnvData;
env.COMPONENT_LOADER = ClientComponentLoader;
env[ENVIRONMENT] = preLoadState?.state?.env || preLoadState?.env || 'dev';

logger.info('initializing app');

const routes = env[MAINTENANCE_MODE] ? maintenanceRoutes : defaultRoutes;

const rootElement = document.getElementById('root');

const hydrateApplication = async () => {
  console.log('TESTING - If you see this, console logging works!');
  console.log(' HYDRATION START - Full preLoadState:', preLoadState);
  console.log(' preLoadState.localeKey:', preLoadState.localeKey);
  console.log(' current i18n.language:', i18n.language);
  console.log(' i18n instance:', i18n);
  console.log(' i18n.isInitialized:', i18n.isInitialized);
  // Ensure client-side i18n matches server-side locale before hydration
  if (preLoadState.localeKey && i18n.isInitialized) {
    if (i18n.language !== preLoadState.localeKey) {
      console.log('Syncing client i18n language to match SSR:', preLoadState.localeKey);
      try {
        await i18n.changeLanguage(preLoadState.localeKey);
        console.log('Sync complete. New i18n.language:', i18n.language);
      } catch (error) {
        console.error('Error changing i18n language:', error);
        throw error;
      }
    } else {
      console.log('i18n already at correct language:', i18n.language);
    }
  } else {
    console.log('Skipping language sync - i18n not initialized or no localeKey');
    console.log('Will rely on i18n default initialization');
  }
  loadableReady(() => {
    hydrateRoot(
      rootElement,
      <HelmetProvider>
        <BrowserRouter>
          <EnvProvider value={env}>
            <I18nextProvider i18n={i18n}>
              <Suspense fallback={<div>Loading...</div>}>
                {(() => {
                  console.log('🎪 About to render App component');
                  console.log('🎪 preLoadState.state:', preLoadState.state);
                  console.log('🎪 preLoadState.localeKey:', preLoadState.localeKey);
                  console.log('🎪 App component reference:', App);
                  return <App store={preLoadState.state} localeKey={preLoadState.localeKey} />;
                })()}
              </Suspense>
            </I18nextProvider>
          </EnvProvider>
        </BrowserRouter>
      </HelmetProvider>,
    );
    initCjs(env[ENVIRONMENT]);
  });
};

if (!rootElement) {
  console.error('Root DOM element not found. Hydration aborted.');
  // return;
}

// Preload matching component before hydration
console.log('Current pathname:', document.location.pathname);
console.log('Available routes:', routes.map((r) => r.path));

const filtered = routes.find((route) => {
  const match = matchPath({
    path: route.path,
  }, document.location.pathname);
  console.log(`Testing route ${route.path} against ${document.location.pathname}:`, match !== null);
  return match !== null;
});

console.log('Filtered route:', filtered);

if (filtered) {
  console.log('Route matched! Preloading component:', filtered.componentKey);
  logger.info('Preloading component: ', filtered.componentKey);
  ClientComponentLoader[filtered.componentKey].load().then(() => {
    hydrateApplication().catch((error) => {
      console.error('Hydration failed:', error);
    });
  });
} else {
  console.log('❌ No route matched! Running hydration anyway...');
  hydrateApplication().catch((error) => {
    console.error('Hydration failed:', error);
  });
}
// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
// serviceWorker.unregister();
